import { IVideoVoRule } from '../../../apis/videoVo/videoVo';
import { IAutomationVariable } from '../../../redux/reducers/automationSlice';

interface VideoVoMapping {
  /** Llave tal como la expone `variables-approbology` del servicio. */
  key: string;
  /** Nombre de la variable con el que se muestra en la revisión automática. */
  variable: string;
}

// Solo estas reglas aportan comentario al desplegable. `vista` coincide con una
// variable que la automatización ya devuelve (`property_view`); las otras dos no
// existen en COUNTRY_VARIABLES, así que se agregan como filas propias.
export const VIDEO_VO_MAPPINGS: Record<string, VideoVoMapping[]> = {
  CO: [
    { key: 'vista', variable: 'property_view' },
    {
      key: 'inmueble_sin_cerramiento_ni_vigilancia_privada',
      variable: 'without_fence_or_private_security',
    },
  ],
  MX: [{ key: 'inmueble_en_zona_de_riesgo', variable: 'risk_zone' }],
};

const isRule = (value: unknown): value is IVideoVoRule =>
  typeof value === 'object' && value !== null;

/**
 * El servicio no expone un estado: lo deriva de `fulfilled`, que según el
 * backend significa que la regla se cumplió y por tanto rechaza.
 */
const resolveStatus = (rule: IVideoVoRule): IAutomationVariable['status'] => {
  if (rule.undetermined) return 'pending';
  return rule.fulfilled ? 'rejected' : 'approved';
};

/**
 * Cruza la respuesta de video_vo con las variables de la revisión automática:
 * si la variable ya existe le agrega el comentario, y si no, la añade como fila
 * nueva. Devuelve siempre un arreglo nuevo.
 */
export const mergeVideoVoComments = (
  variables: IAutomationVariable[],
  rulesByCountry: Record<string, unknown> | undefined,
  country: string
): IAutomationVariable[] => {
  const mappings = VIDEO_VO_MAPPINGS[country];
  if (!mappings || !rulesByCountry) return variables;

  const merged = [...variables];

  mappings.forEach(({ key, variable }) => {
    const rule = rulesByCountry[key];
    if (!isRule(rule)) return;

    const comment = rule.comment?.trim();
    if (!comment) return;

    const existing = merged.findIndex(item => item.variable === variable);

    if (existing >= 0) {
      merged[existing] = { ...merged[existing], comment };
      return;
    }

    merged.push({
      variable,
      status: resolveStatus(rule),
      approved: rule.fulfilled === undefined ? null : !rule.fulfilled,
      reviewed_reasons: [],
      rejection_reasons: [],
      comment,
    });
  });

  return merged;
};
